import React, { useState } from 'react';
import {
  Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, CircularProgress, Alert, TextField, Divider, Autocomplete, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemText, ListItemSecondaryAction,
  Chip, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, CloudUpload as UploadIcon, Description as DocIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { splitBySize, oversizeMessage } from '../../utils/fileSizeLimit';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { filesFromEvent } from '../../utils/dropPasteFiles';
import { isPrimaryDocType } from '../../utils/docTypeGroups';
import {
  mergeCustomFields, mergeRequiredDocs, buildServiceRow, customerTotals
} from './workOrderCustomer';

const fontSize = { xs: '0.75rem', sm: '0.875rem', md: '1rem' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(amount || 0);

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return (!s || s.toLowerCase() === 'null') ? '' : s;
};

/**
 * One applicant's services, details, custom fields and documents — the per-customer section of the
 * Create Work Order page, as a controlled component. `customer` is the state, `onChange(updater)`
 * receives a function (prev → next). Uploading a passport/EID etc. OCRs it and fills the empty
 * identity fields; a document whose detected name differs from the entered one is kept but flagged.
 */
const WorkOrderCustomerForm = ({
  customer,
  onChange,
  availableServices = [],
  documentTypes = [],
  showThaklisServices = false,
  employeeTrackingActive = false,
  onError
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [markupAmount, setMarkupAmount] = useState('');
  const [serviceTab, setServiceTab] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [dragActive, setDragActive] = useState(false);
  // Detected names that differ from the customer's — informational only on this single-customer form.
  const [nameMismatches, setNameMismatches] = useState([]);

  const reportError = (msg) => onError && onError(msg);
  const update = (fn) => onChange(fn);
  const setField = (field, value) => update(c => ({ ...c, [field]: value }));

  // ---- services ----
  const serviceOptions = showThaklisServices
    ? availableServices.filter(s => (serviceTab === 0 ? s.type === 'INTERNAL' : s.type !== 'INTERNAL'))
    : availableServices.filter(s => s.type === 'INTERNAL');

  const handleAddService = () => {
    if (!selectedService) return;
    const row = buildServiceRow(selectedService, markupAmount);
    update(c => {
      // Seed shared field values from the org defaults on the added service — never overwrite a typed value.
      const nextValues = { ...(c.fieldValues || {}) };
      row.customFields.forEach(f => {
        const def = f.orgDefault;
        const current = nextValues[f.fieldId];
        if (def != null && String(def).trim() !== '' && (current == null || String(current).trim() === '')) {
          nextValues[f.fieldId] = def;
        }
      });
      return { ...c, services: [...c.services, row], fieldValues: nextValues };
    });
    setSelectedService(null);
    setMarkupAmount('');
  };

  const handleRemoveService = (rowId) => update(c => ({ ...c, services: c.services.filter(s => s.id !== rowId) }));

  const handleFieldValue = (fieldId, value) =>
    update(c => ({ ...c, fieldValues: { ...(c.fieldValues || {}), [fieldId]: value } }));

  // ---- documents ----
  // `forced` ({ code, label }) comes from a required-document slot; otherwise the "add another" type is used.
  const handleFileUpload = async (event, forced = null) => {
    const picked = Array.from(event.target.files || []);
    if (event.target) event.target.value = '';
    if (picked.length === 0) return;
    const { ok: files, tooBig } = splitBySize(picked);
    if (tooBig.length) reportError(tooBig.map(oversizeMessage).join(' '));
    if (files.length === 0) return;

    const docTypeCode = forced?.code || selectedDocType;
    if (!docTypeCode) { reportError('Please select a document type'); return; }
    const docType = documentTypes.find(d => (d.code || d.id) === docTypeCode);
    const baseId = Date.now();
    const staged = files.map((file, i) => ({
      id: baseId + i,
      file,
      fileName: file.name,
      fileSize: file.size,
      documentType: docTypeCode,
      documentTypeName: forced?.label || docType?.label || docType?.name || docTypeCode,
      extracting: true,
      documentNumber: null,
      issueDate: null,
      expiryDate: null
    }));
    update(c => ({ ...c, documents: [...c.documents, ...staged] }));
    if (!forced) setSelectedDocType('');

    // Best-effort OCR per file: fill empty identity fields and keep each doc's own number/issue/expiry.
    await Promise.all(staged.map(async (doc) => {
      try {
        const response = await ApiService.extractDocument(doc.file, 'GENERAL', doc.documentType);
        const extracted = response?.data?.data || {};
        const failure = response?.data?.success === false
          ? (response.data.error || response.data.message)
          : (extracted._extractionSkipped
            ? (extracted._extractionSkippedReason || 'Automatic field extraction was skipped — enter the fields manually.')
            : null);
        if (failure) reportError(failure);

        const fullName = clean(extracted.fullName);
        const passportNumber = clean(extracted.passportNumber);
        const emiratesIdNumber = clean(extracted.emiratesIdNumber);
        const phoneNumber = clean(extracted.phoneNumber);
        const email = clean(extracted.email);
        const meta = {
          extracting: false,
          documentNumber: resolveExtractedDocNumber(extracted, doc.documentType),
          issueDate: clean(extracted.issueDate),
          expiryDate: clean(extracted.expiryDate)
        };

        update(c => {
          const existingName = c.name?.trim() || '';
          const docs = c.documents.map(d => (d.id === doc.id ? { ...d, ...meta } : d));
          if (existingName && fullName && existingName.toLowerCase() !== fullName.toLowerCase()) {
            setNameMismatches(prev => (prev.some(m => m.docId === doc.id)
              ? prev
              : [...prev, { docId: doc.id, fileName: doc.fileName, detected: fullName, existing: existingName }]));
            return { ...c, documents: docs };
          }
          return {
            ...c,
            name: existingName ? c.name : (fullName || c.name),
            passportNumber: c.passportNumber?.trim() ? c.passportNumber : (passportNumber || c.passportNumber),
            emiratesId: c.emiratesId?.trim() ? c.emiratesId : (emiratesIdNumber || c.emiratesId),
            phone: c.phone?.trim() ? c.phone : (phoneNumber || c.phone),
            email: c.email?.trim() ? c.email : (email || c.email),
            documents: docs
          };
        });
      } catch (err) {
        console.error('Document extraction failed:', err);
        reportError('Document extraction failed — enter the fields manually.');
        update(c => ({ ...c, documents: c.documents.map(d => (d.id === doc.id ? { ...d, extracting: false } : d)) }));
      }
    }));
  };

  // Files dragged in or pasted (Ctrl/Cmd+V of a copied image) go through the same staging + OCR path.
  const handleDroppedFiles = async (e, forced = null) => {
    const files = filesFromEvent(e);
    if (!files.length) return;
    e.preventDefault();
    if (!forced && !selectedDocType) { reportError('Please select a document type'); return; }
    await handleFileUpload({ target: { files, value: '' } }, forced);
  };

  const handleRemoveDocument = (docId) => {
    update(c => ({ ...c, documents: c.documents.filter(d => d.id !== docId) }));
    setNameMismatches(prev => prev.filter(m => m.docId !== docId));
  };

  // ---- derived ----
  const totals = customerTotals(customer);
  const mergedFields = mergeCustomFields(customer);
  const mergedDocs = mergeRequiredDocs(customer);
  const mergedDocCodes = new Set(mergedDocs.map(d => d.code));
  const uploadedCodes = new Set(customer.documents.map(d => d.documentType));
  const extraDocTypes = documentTypes.filter(dt => {
    const code = dt.code || dt.id;
    return !mergedDocCodes.has(code) && !uploadedCodes.has(code);
  });
  const extraUploadedDocs = customer.documents.filter(d => !mergedDocCodes.has(d.documentType));

  const renderUploadedDoc = (doc) => (
    <ListItem key={doc.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
      <DocIcon sx={{ mr: 2, color: 'secondary.main' }} />
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{doc.fileName}</Typography>
            <Chip label={doc.documentTypeName} size="small" sx={{ fontSize: '0.65rem' }} />
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={doc.extracting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircularProgress size={12} />
            <Typography sx={{ fontSize: fontSize.xs, color: 'info.main' }}>Extracting details…</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
              {`Size: ${formatFileSize(doc.fileSize)}`}{doc.documentNumber ? ` · Doc No: ${doc.documentNumber}` : ''}
            </Typography>
            {(doc.issueDate || doc.expiryDate) && (
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                {[doc.issueDate ? `Issued ${doc.issueDate}` : null, doc.expiryDate ? `Expires ${doc.expiryDate}` : null]
                  .filter(Boolean).join(' · ')}
              </Typography>
            )}
          </>
        )}
      />
      <ListItemSecondaryAction>
        {doc.file && (
          <IconButton edge="end" size="small" onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')} sx={{ mr: 0.5 }}>
            <DocIcon fontSize="small" color="info" />
          </IconButton>
        )}
        <IconButton edge="end" size="small" onClick={() => handleRemoveDocument(doc.id)}>
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  const docTypeMenuItems = () => {
    const primary = extraDocTypes.filter(isPrimaryDocType);
    const secondary = extraDocTypes.filter(dt => !isPrimaryDocType(dt));
    const item = (dt) => (
      <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ fontSize: '0.85rem' }}>
        {dt.label || dt.name || dt.code}
      </MenuItem>
    );
    const items = [];
    if (primary.length) {
      items.push(<MenuItem key="_p" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', opacity: '1 !important', py: 0.5 }}>PRIMARY DOCUMENTS</MenuItem>);
      primary.forEach(dt => items.push(item(dt)));
    }
    if (secondary.length) {
      items.push(<MenuItem key="_s" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', opacity: '1 !important', py: 0.5, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>SECONDARY DOCUMENTS</MenuItem>);
      secondary.forEach(dt => items.push(item(dt)));
    }
    return items;
  };

  return (
    <Box>
      {/* Services */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
        Services for {customer.name || 'this Customer'}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
        {showThaklisServices && (
          <>
            <Tabs value={serviceTab} onChange={(e, v) => { setServiceTab(v); setSelectedService(null); }} sx={{ mb: 1, minHeight: 36 }}>
              <Tab label="Internal Services" sx={{ minHeight: 36, py: 0 }} />
              <Tab label="Thaklis Services" sx={{ minHeight: 36, py: 0 }} />
            </Tabs>
            {serviceTab === 1 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These services are provided by Thaklis — link each one to its ticket from the task once the customer is added.
              </Alert>
            )}
          </>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={serviceOptions}
              getOptionLabel={(o) => o.name}
              value={selectedService}
              onChange={(e, v) => setSelectedService(v)}
              size="small"
              renderInput={(params) => <TextField {...params} label="Select Service" placeholder="Choose a service..." />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Markup (AED)" type="number"
              value={markupAmount} onChange={(e) => setMarkupAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            {selectedService && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                  {formatCurrency((selectedService.service_charge || 0) + (selectedService.govt_charge || 0) + (parseFloat(markupAmount) || 0))}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={handleAddService} disabled={!selectedService}>
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {customer.services.length === 0 ? (
        <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 3 }}>
          No services added yet. Select a service above to add.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.8rem' } }}>
                <TableCell>Service</TableCell>
                <TableCell align="right">Service Chg</TableCell>
                <TableCell align="right">Govt Chg</TableCell>
                <TableCell align="right">Markup</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center" width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {customer.services.map((item) => (
                <TableRow hover key={item.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{item.service_name}</Typography>
                    <Chip label={item.service_type} size="small" sx={{ fontSize: '0.65rem', height: 18, mt: 0.5 }}
                      color={item.service_type === 'THAKLIS' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.service_charge)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>{formatCurrency(item.govt_charge)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', color: item.markup_amount > 0 ? 'success.main' : 'text.disabled' }}>
                    {item.markup_amount > 0 ? formatCurrency(item.markup_amount) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(item.tax_value)}</Typography>
                    {item.tax_on_markup > 0 && (
                      <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>(+{formatCurrency(item.tax_on_markup)})</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatCurrency(item.line_total)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="error" onClick={() => handleRemoveService(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>Customer Subtotal:</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>{formatCurrency(totals.grandTotal)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Customer information */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2, color: 'secondary.main' }}>Customer Information</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Full Name" required value={customer.name}
            onChange={(e) => setField('name', e.target.value)} placeholder="Enter full name" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Passport Number" value={customer.passportNumber}
            onChange={(e) => setField('passportNumber', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Emirates ID" value={customer.emiratesId}
            onChange={(e) => setField('emiratesId', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Phone Number" value={customer.phone}
            onChange={(e) => setField('phone', e.target.value)} placeholder="Optional" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Email" type="email" value={customer.email}
            onChange={(e) => setField('email', e.target.value)} placeholder="Optional" />
        </Grid>
      </Grid>
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 1 }}>
        Tip: upload the passport or Emirates ID below and the empty fields are filled in automatically.
      </Typography>
      {employeeTrackingActive && !customer.passportNumber?.trim() && !customer.emiratesId?.trim() && (
        <Alert severity="warning" sx={{ mt: 2, fontSize: fontSize.xs }}>
          No unique identifier for this applicant. Add a Passport Number or Emirates ID — without one, the employee and
          their documents can't be tracked, so you won't get expiry reminders for their documents.
        </Alert>
      )}
      {nameMismatches.map(m => (
        <Alert key={m.docId} severity="info" sx={{ mt: 1.5, fontSize: fontSize.xs }}
          onClose={() => setNameMismatches(prev => prev.filter(x => x.docId !== m.docId))}>
          {m.fileName} looks like it belongs to <strong>{m.detected}</strong>, not {m.existing}. It stays with this
          customer — remove it if it was added by mistake.
        </Alert>
      ))}

      {/* Custom fields */}
      {mergedFields.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>Service Information</Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
            Details needed for this customer&apos;s services. Shared fields are entered once.
          </Typography>
          <Grid container spacing={2}>
            {mergedFields.map((field) => {
              const val = customer.fieldValues?.[field.fieldId] || '';
              const label = field.label + (field.required ? ' *' : '');
              return (
                <Grid item xs={12} sm={field.fieldType === 'TEXTAREA' ? 12 : 4} key={field.fieldId}>
                  {field.fieldType === 'SELECT' ? (
                    <TextField select fullWidth size="small" label={label} value={val}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {(field.options || []).map((opt, oi) => (
                        <MenuItem key={oi} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                          {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : field.fieldType === 'DATE' ? (
                    <TextField fullWidth size="small" type="date" label={label} value={val}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)} InputLabelProps={{ shrink: true }} />
                  ) : (
                    <TextField fullWidth size="small" label={label} value={val}
                      type={field.fieldType === 'NUMBER' ? 'number' : 'text'}
                      multiline={field.fieldType === 'TEXTAREA'} rows={field.fieldType === 'TEXTAREA' ? 2 : undefined}
                      onChange={(e) => handleFieldValue(field.fieldId, e.target.value)} />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Documents */}
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
        Documents for {customer.name || 'this Customer'}
      </Typography>
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 2 }}>
        {mergedDocs.length > 0
          ? 'Attach the documents these services need. Required documents must be uploaded before the customer can be added.'
          : 'Upload documents specific to this customer.'}
      </Typography>

      {mergedDocs.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {mergedDocs.map((reqDoc) => {
            const docsForType = customer.documents.filter(d => d.documentType === reqDoc.code);
            const uploaded = docsForType.length > 0;
            const forced = { code: reqDoc.code, label: reqDoc.label };
            return (
              <Paper key={reqDoc.code} variant="outlined" sx={{
                p: 1.5, mb: 1, borderRadius: 2, borderStyle: uploaded ? 'solid' : 'dashed',
                borderColor: uploaded ? 'success.main' : (reqDoc.required ? 'warning.main' : 'divider')
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {uploaded
                      ? <CheckIcon sx={{ fontSize: '1.05rem', color: 'success.main' }} />
                      : <DocIcon sx={{ fontSize: '1.05rem', color: reqDoc.required ? 'warning.main' : 'text.disabled' }} />}
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>
                      {reqDoc.label}{reqDoc.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                    </Typography>
                  </Box>
                  <Chip size="small" variant={uploaded ? 'filled' : 'outlined'}
                    label={uploaded ? 'Uploaded' : (reqDoc.required ? 'Required' : 'Optional')}
                    color={uploaded ? 'success' : (reqDoc.required ? 'warning' : 'default')}
                    sx={{ fontSize: '0.62rem', height: 18 }} />
                </Box>
                {uploaded ? (
                  <List dense sx={{ py: 0 }}>{docsForType.map(renderUploadedDoc)}</List>
                ) : (
                  <Box tabIndex={0}
                    onPaste={(e) => handleDroppedFiles(e, forced)}
                    onDrop={(e) => { e.preventDefault(); handleDroppedFiles(e, forced); }}
                    onDragOver={(e) => e.preventDefault()}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                      border: '1px dashed', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, outline: 'none',
                      '&:focus': { borderColor: 'primary.main' }
                    }}>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                      Drag a file here, paste a copied image (Ctrl/⌘+V), or use Upload
                    </Typography>
                    <Button variant="outlined" size="small" component="label" startIcon={<UploadIcon />} sx={{ flexShrink: 0 }}>
                      Upload
                      <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={(e) => handleFileUpload(e, forced)} />
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {extraDocTypes.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>Add another document</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type</InputLabel>
                <Select value={selectedDocType} label="Document Type" onChange={(e) => setSelectedDocType(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  {docTypeMenuItems()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="contained" component="label" fullWidth startIcon={<UploadIcon />} disabled={!selectedDocType}>
                Upload Document(s)
                <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e)} />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box tabIndex={0}
                onPaste={(e) => handleDroppedFiles(e)}
                onDrop={(e) => { setDragActive(false); handleDroppedFiles(e); }}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                sx={{
                  border: '1px dashed', borderColor: dragActive ? 'primary.main' : 'divider',
                  bgcolor: dragActive ? 'action.hover' : 'transparent', borderRadius: 1, p: 1.25,
                  textAlign: 'center', outline: 'none', '&:focus': { borderColor: 'primary.main' }
                }}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>
                  {selectedDocType
                    ? 'Drag files here, or click this box and paste a copied image (Ctrl/⌘+V)'
                    : 'Select a document type first, then drag or paste files here'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {extraUploadedDocs.length > 0 && <List dense>{extraUploadedDocs.map(renderUploadedDoc)}</List>}
    </Box>
  );
};

export default WorkOrderCustomerForm;
